import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaInstagram, FaFacebookF, FaTwitter } from 'react-icons/fa';
import logoImage from '../assets/logo.jpg';

const FooterContainer = styled.footer`
  background-color: var(--background-darker);
  padding: 60px 0 20px;
  color: var(--text-secondary);
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
`;

const FooterColumn = styled.div`
  margin-bottom: 30px;
`;

const LogoImage = styled.img`
  height: 40px;
  margin-right: 10px;
  border-radius: 50%;
  transition: var(--transition);
`;

const FooterLogo = styled(Link)`
  display: flex;
  align-items: center;
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  
  &:hover {
    color: var(--text-primary);
    
    ${LogoImage} {
      filter: brightness(1.4) contrast(1.2) invert(1);
      transform: scale(1.05);
    }
  }
`;

const FooterDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const FooterTitle = styled.h4`
  color: var(--text-primary);
  font-size: 1.2rem;
  margin-bottom: 20px;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 2px;
    background-color: var(--primary-color);
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
`;

const FooterLink = styled.li`
  margin-bottom: 12px;
  
  a {
    color: var(--text-secondary);
    font-size: 0.9rem;
    transition: var(--transition);
    display: inline-block;
    
    &:hover {
      color: var(--primary-color);
      transform: translateX(5px);
    }
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  
  svg {
    color: var(--primary-color);
    margin-right: 10px;
    font-size: 1.2rem;
  }
  
  p {
    color: var(--text-secondary);
    font-size: 0.9rem;
    margin: 0;
  }
  
  a {
    color: var(--text-secondary);
    
    &:hover {
      color: var(--primary-color);
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  margin-top: 20px;
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: var(--background-lighter);
  color: var(--text-secondary);
  margin-right: 10px;
  transition: var(--transition);
  
  &:hover {
    background-color: var(--primary-color);
    color: white;
    transform: translateY(-3px);
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: var(--background-lighter);
  margin: 20px auto 20px;
  max-width: 1200px;
`;

const Copyright = styled.div`
  text-align: center;
  padding: 20px;
  color: var(--text-tertiary);
  font-size: 0.9rem;
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterContent>
        <FooterColumn>
          <FooterLogo to="/">
            <LogoImage src={logoImage} alt="Istri Wala Logo" />
            Istri Wala
          </FooterLogo>
          <FooterDescription>
            Complete laundry solutions including steam ironing, dry cleaning, laundry services, and shoe cleaning with free pickup and delivery within 5km. Experience professional care for all your garments.
          </FooterDescription>
          <SocialLinks>
            <SocialIcon href="https://www.instagram.com/istriwala8" target="_blank" rel="noopener noreferrer">
              <FaInstagram />
            </SocialIcon>
            <SocialIcon href="#" target="_blank" rel="noopener noreferrer">
              <FaFacebookF />
            </SocialIcon>
            <SocialIcon href="#" target="_blank" rel="noopener noreferrer">
              <FaTwitter />
            </SocialIcon>
          </SocialLinks>
        </FooterColumn>
        
        <FooterColumn>
          <FooterTitle>Quick Links</FooterTitle>
          <FooterLinks>
            <FooterLink><Link to="/">Home</Link></FooterLink>
            <FooterLink><Link to="/services">Services</Link></FooterLink>
            <FooterLink><Link to="/about">About Us</Link></FooterLink>
            <FooterLink><Link to="/pricing">Pricing</Link></FooterLink>
            <FooterLink><Link to="/contact">Contact</Link></FooterLink>
          </FooterLinks>
        </FooterColumn>
        
        <FooterColumn>
          <FooterTitle>Services</FooterTitle>
          <FooterLinks>
            <FooterLink><Link to="/services">Steam Ironing</Link></FooterLink>
            <FooterLink><Link to="/services">Dry Cleaning</Link></FooterLink>
            <FooterLink><Link to="/services">Laundry</Link></FooterLink>
            <FooterLink><Link to="/services">Shoes Cleaning</Link></FooterLink>
            <FooterLink><Link to="/services">Express Service</Link></FooterLink>
          </FooterLinks>
        </FooterColumn>
        
        <FooterColumn>
          <FooterTitle>Contact Us</FooterTitle>
          <ContactItem>
            <FaPhoneAlt />
            <p><a href="tel:9755504400">+91 975 550 4400</a></p>
          </ContactItem>
          <ContactItem>
            <FaEnvelope />
            <p><a href="mailto:istriwala453@gmail.com">istriwala453@gmail.com</a></p>
          </ContactItem>
          <ContactItem>
            <FaMapMarkerAlt />
            <p>Opposite Sacha Sauda Gurudwara, near Neta Ji Chowk, Katora Talab, Civil Lines, Raipur</p>
          </ContactItem>
          <ContactItem>
            <FaInstagram />
            <p><a href="https://www.instagram.com/istriwala8" target="_blank" rel="noopener noreferrer">@istriwala8</a></p>
          </ContactItem>
        </FooterColumn>
      </FooterContent>
      
      <Divider />
      
      <Copyright>
        &copy; {new Date().getFullYear()} Istri Wala. All rights reserved.
      </Copyright>
    </FooterContainer>
  );
};

export default Footer;
